// ─── CONFIGURATION DE L'ARC DE REMPLISSAGE ──────────────────────────────────
// Ces constantes définissent la plage angulaire de remplissage de l'arc.
// 0% XP → START_DEG de l'arc rempli, 100% → END_DEG de l'arc rempli.
// Ajustez ces valeurs visuellement pour couper le remplissage aux bords droits.
const FILL_CONFIG = {
  START_DEG: 90, // degrés à 0% de remplissage
  END_DEG: 270,  // degrés à 100% de remplissage
};

// ==========================================
// LEVEL ANIMATION SYSTEM
// ==========================================
// Module d'animation du remplissage des niveaux (carte en serpentin).
// Utilise directement le DOM (bypass Vue.js) pour garantir 60fps sans saccades.
//
// Usage dans le composant :
//   import { createLevelAnimationSystem } from './scripts/LevelAnimationSystem.js'
//   const { system, pathSegments, basePathSegment } = createLevelAnimationSystem()
//   watch(userLevelData, (val) => system.play(target), { deep: true, immediate: true })
// ==========================================

/**
 * Crée une instance du système d'animation.
 * Prend en paramètre les refs Vue pointant vers les éléments DOM.
 *
 * @param {import('vue').Ref<HTMLElement[]>} pathSegmentsRef - Ref[] des segments de niveaux
 * @param {import('vue').Ref<HTMLElement|null>} basePathSegmentRef - Ref de l'ancre de base
 * @returns {{ play(target: number): void, config: object }}
 */
export function createLevelAnimationSystem(pathSegmentsRef, basePathSegmentRef, endPathSegmentRef) {
  const system = {
    config: {
      delayMs: 1000,    // Délai avant le début de l'animation
      baseDurationMs: 500, // Durée de base (0.5s)
      durationPerLevelMs: 500, // Durée ajoutée par niveau (0.5s)
      startValue: -100, // -100 = état totalement vide (bas de l'ancre)
    },
    timeout: null,

    // Calcule le CSS de masque pour un segment donné (pur, sans réactivité)
    calculateMaskCSS(levelIndex, totalAnimated) {
      const fillPercent = Math.max(0, Math.min(100, totalAnimated - (levelIndex * 100 - 50)));
      if (fillPercent >= 100) return 'none';
      if (fillPercent <= 0) return 'hidden';

      const { START_DEG, END_DEG } = FILL_CONFIG;
      const fillDeg = START_DEG + (fillPercent / 100) * (END_DEG - START_DEG);

      return levelIndex % 2 === 0
        ? `conic-gradient(from 90deg at 66.66% 50%, black 0deg, black ${fillDeg}deg, transparent ${fillDeg}deg, transparent 360deg)`
        : `conic-gradient(from 270deg at 33.33% 50%, transparent 0deg, transparent ${360 - fillDeg}deg, black ${360 - fillDeg}deg, black 360deg)`;
    },

    // Met à jour directement le style des éléments DOM (zéro overhead Vue)
    updateSegmentsStyle(totalAnimated) {
      // 1. Ancre de base (segment vertical de départ)
      const baseEl = basePathSegmentRef.value;
      if (baseEl) {
        const baseFillPercent = Math.max(0, Math.min(100, (totalAnimated + 100) * 2));
        if (baseFillPercent <= 0) {
          baseEl.style.display = 'none';
        } else {
          baseEl.style.display = 'block';
          const css = `linear-gradient(to top, black 0%, black ${baseFillPercent}%, transparent ${baseFillPercent}%, transparent 100%)`;
          baseEl.style.maskImage = css;
          baseEl.style.webkitMaskImage = css;
        }
      }

      // 2. Segments de niveaux (arcs en serpentin)
      const segments = pathSegmentsRef.value;
      if (!segments) return;
      segments.forEach((el, index) => {
        if (!el) return;
        const css = this.calculateMaskCSS(index, totalAnimated);
        if (css === 'hidden') {
          el.style.display = 'none';
        } else {
          el.style.display = 'block';
          el.style.maskImage = css;
          el.style.webkitMaskImage = css;
        }
      });

      // 3. Segment de fin (Top Cap)
      if (endPathSegmentRef && endPathSegmentRef.value) {
        const endEl = endPathSegmentRef.value;
        const index = segments.length; // Il agit comme le (N+1)-ième segment
        
        const startTotal = index * 100 - 50;
        const endFillPercent = Math.max(0, Math.min(100, (totalAnimated - startTotal) * 2));

        if (endFillPercent >= 100) {
          endEl.style.maskImage = 'none';
          endEl.style.webkitMaskImage = 'none';
          endEl.style.display = 'block';
        } else if (endFillPercent <= 0) {
          endEl.style.display = 'none';
        } else {
          endEl.style.display = 'block';
          const { START_DEG, END_DEG } = FILL_CONFIG;
          const fillDeg = START_DEG + (endFillPercent / 100) * (END_DEG - START_DEG);
          const css = index % 2 === 0
            ? `conic-gradient(from 90deg at 66.66% 50%, black 0deg, black ${fillDeg}deg, transparent ${fillDeg}deg, transparent 360deg)`
            : `conic-gradient(from 270deg at 33.33% 50%, transparent 0deg, transparent ${360 - fillDeg}deg, black ${360 - fillDeg}deg, black 360deg)`;
          
          endEl.style.maskImage = css;
          endEl.style.webkitMaskImage = css;
        }
      }
    },

    // Lance l'animation vers la valeur cible, avec délai et ease-out
    play(target) {
      if (this.timeout) clearTimeout(this.timeout);

      // Forcer l'état totalement vide pendant le délai
      this.updateSegmentsStyle(this.config.startValue);

      this.timeout = setTimeout(() => {
        const startTime = performance.now();
        
        // Calcul de la durée en fonction du nombre de niveaux à remplir (1 niveau = 100)
        const levelsToFill = Math.max(0, target / 100);
        // On borne la durée totale (ex: entre 1s et 3s max)
        const totalDurationMs = Math.min(
          this.config.baseDurationMs + (levelsToFill * this.config.durationPerLevelMs),
          4000
        );

        const step = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / totalDurationMs, 1);

          // Ease-out quart : ralentissement progressif à la fin
          const easeOut = 1 - Math.pow(1 - progress, 4);

          const currentAnimated = this.config.startValue + (target - this.config.startValue) * easeOut;
          this.updateSegmentsStyle(currentAnimated);

          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, this.config.delayMs);
    }
  };

  return system;
}
